import { FC, FormEvent, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { Swiper, SwiperSlide } from "swiper/react"
import { Swiper as SwiperType } from "swiper/types"
import { parseAxiosError } from "@/helpers/parseAxiosError"
import { showToast } from "@/helpers/toast"
import { useOtp } from "@/hooks/useOtp"
import { useModals } from "@/modals"
import {
  useSendEmailCodeCreateMutation,
  useSendEmailCodeLoginMutation,
  useVerifyEmailCodeMutation
} from "@/store/apis/authAPI/auth.api"
import { Button } from "@/ui/Button"
import type { IAuthMode } from "../types"
import styles from "./EmailOtpForm.module.scss"

const OTP_LENGTH = 6
const SLIDE_SPEED = 400

interface IEmailOtpForm {
  mode: IAuthMode
  initialEmail?: string
}

export const EmailOtpForm: FC<IEmailOtpForm> = ({ mode, initialEmail }) => {
  const { t } = useTranslation()
  const { openModal } = useModals()

  const refSwiper = useRef<SwiperType | null>(null)

  const [email, setEmail] = useState<string>(initialEmail ?? "")
  const {
    otp,
    otpValue,
    otpRefs,
    handleChange,
    handleKeyDown,
    handlePaste,
    reset,
    focusFirst
  } = useOtp(OTP_LENGTH)

  const [sendLogin, { isLoading: isSendingLogin }] =
    useSendEmailCodeLoginMutation()
  const [sendCreate, { isLoading: isSendingCreate }] =
    useSendEmailCodeCreateMutation()
  const [verifyCode, { isLoading: isVerifying }] = useVerifyEmailCodeMutation()

  const sendCode = mode === "login" ? sendLogin : sendCreate
  const isSending = mode === "login" ? isSendingLogin : isSendingCreate

  const i18nPrefix = mode === "login" ? "login" : "register"

  const handleEmailSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!email || isSending) return

    try {
      await sendCode({ email }).unwrap()
      refSwiper.current?.slideTo(1)
      setTimeout(focusFirst, SLIDE_SPEED)
    } catch (error) {
      const { statusCode } = parseAxiosError(error)

      if (mode === "login" && statusCode === 404) {
        openModal("authRedirectModal", { mode: "toRegister", email })
        return
      }
      if (mode === "register" && statusCode === 409) {
        openModal("authRedirectModal", { mode: "toLogin", email })
        return
      }
      showToast.error(t("errors.authFailed"))
    }
  }

  const handleVerifyCode = async () => {
    try {
      await verifyCode({ email, code: otpValue }).unwrap()
    } catch {
      showToast.error(t("errors.authFailed"))
    }
  }

  const handleCodeSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (otpValue.length < OTP_LENGTH || isVerifying) return
    handleVerifyCode()
  }

  const handleBack = () => {
    refSwiper.current?.slideTo(0)
    reset()
  }

  return (
    <Swiper
      className={styles.EmailOtpForm}
      allowTouchMove={false}
      simulateTouch={false}
      speed={SLIDE_SPEED}
      autoHeight
      spaceBetween={26}
      onSwiper={(swiper) => {
        refSwiper.current = swiper
      }}
    >
      <SwiperSlide>
        <form
          className={styles.EmailOtpForm_slide}
          onSubmit={handleEmailSubmit}
        >
          <input
            className={styles.EmailOtpForm_input}
            type="email"
            placeholder={t(`${i18nPrefix}.email.placeholder`)}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Button
            className={styles.EmailOtpForm_button}
            disabled={isSending || !email}
            type="submit"
          >
            {t(`${i18nPrefix}.email.sendCode`)}
          </Button>
        </form>
      </SwiperSlide>

      <SwiperSlide>
        <form className={styles.EmailOtpForm_slide} onSubmit={handleCodeSubmit}>
          <p className={styles.EmailOtpForm_hint}>
            {t(`${i18nPrefix}.email.codeSentTo`)} {email}
          </p>

          <div className={styles.EmailOtpForm_otpGroup}>
            {otp.map((digit, index) => (
              <input
                key={`otp-${index}`}
                ref={(el) => {
                  otpRefs.current[index] = el
                }}
                className={styles.EmailOtpForm_otpBox}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
              />
            ))}
          </div>

          <Button
            className={styles.EmailOtpForm_button}
            disabled={isVerifying || otpValue.length < OTP_LENGTH}
            type="submit"
          >
            {t(`${i18nPrefix}.email.verify`)}
          </Button>
          <Button
            className={styles.EmailOtpForm_back}
            onClick={handleBack}
            type="button"
          >
            {t(`${i18nPrefix}.email.back`)}
          </Button>
        </form>
      </SwiperSlide>
    </Swiper>
  )
}
